import { ReactNode } from 'react';
import { CheckCircleIcon } from '@nimbus-ds/icons';
import { Prompt } from 'react-router';
import { useNavegate } from 'App/hooks';
import {
  CancelAndConfirmButtons,
  HeaderContent,
  HeaderTop,
  IonPageStratus,
} from 'commons/components';
import { useTranslationCommon } from 'commons/hooks';
import { StatusType } from 'commons/types';
import { useTranslationCatalog } from 'domains/Catalog/hooks';

interface MetaplanPageProps {
  mode: 'edit' | 'add';
  children: ReactNode;
  isDirty: boolean;
  saveStatus: StatusType;
  loadStatus: StatusType;
  onSave: () => void;
}

export function MetaplanPage({
  mode,
  children,
  isDirty,
  saveStatus,
  loadStatus,
  onSave,
}: Readonly<MetaplanPageProps>): JSX.Element {
  const { goBack } = useNavegate();
  const tCommon = useTranslationCommon('exitEdit');
  const t = useTranslationCatalog();

  const isSaving = saveStatus === 'loading';
  const saveMode = isSaving ? 'saving' : 'save';

  const title = t(`subscriptions.form.${mode}.title`);
  const saveText = t(`subscriptions.form.${mode}.${saveMode}`);

  const disabledAction = (!isDirty && mode === 'edit') || isSaving;

  const headerAction = {
    onClick: onSave,
    children: saveText,
    icon: CheckCircleIcon,
    spinner: isSaving,
    disabled: disabledAction,
  };

  return (
    <IonPageStratus
      width="small"
      headerTop={
        <HeaderTop navigation={{ onClick: goBack }} mainAction={headerAction} />
      }
      headerContent={<HeaderContent title={title} />}
      headerFixed
    >
      {children}
      <Prompt
        when={isDirty && saveStatus !== 'success'}
        message={tCommon('info')}
      />
      {loadStatus === 'success' && (
        <CancelAndConfirmButtons
          isLoading={isSaving}
          confirmText={saveText}
          isCancelDisabled={isSaving}
          isConfirmDisabled={disabledAction}
          onCancel={goBack}
          onConfirm={onSave}
        />
      )}
      {loadStatus === 'loading' && <CancelAndConfirmButtons.Skeleton />}
    </IonPageStratus>
  );
}
